import React, { Component, useEffect } from "react";
import "@fontsource/inter";
import {
  createTheme,
  ThemeProvider,
  THEME_ID as MATERIAL_THEME_ID,
} from "@mui/material/styles";
import { CssVarsProvider as JoyCssVarsProvider } from "@mui/joy/styles";
import CssBaseline from "@mui/joy/CssBaseline";
import { CircularProgress, Box } from "@mui/joy";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate } from "react-router-dom";
import { Map } from "./components/Map";
import { ProfilePage } from "./pages/ProfilePage";
import { SearchPage } from "./pages/SearchPage";
import { ActivityFeedPage } from "./pages/ActivityFeedPage";
import { LandingPage } from "./pages/LandingPage";
import { AuthPage } from "./pages/AuthPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { WaitlistPage } from "./pages/WaitlistPage";
import { CompleteProfilePage } from "./pages/CompleteProfilePage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { AuthConfirmPage } from "./pages/AuthConfirmPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { ProtectedRoute } from "./components/auth";
import { useAuthStore } from "./store/authStore";
import { CommandBar } from "./components/CommandBar";
import { useCommandBarShortcut } from "./hooks/useCommandBarShortcut";

const materialTheme = createTheme();
const queryClient = new QueryClient();

// Error boundary to prevent white-screen crashes
interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Application error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            backgroundColor: "#FDF8F5",
            gap: 2,
            padding: 3,
            textAlign: "center",
          }}
        >
          <h2>Something went wrong</h2>
          <p>Please try refreshing the page.</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              cursor: "pointer",
              backgroundColor: "#fff",
            }}
          >
            Refresh Page
          </button>
        </Box>
      );
    }
    return this.props.children;
  }
}

// Loading screen component
const LoadingScreen: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      backgroundColor: "#FDF8F5",
    }}
  >
    <CircularProgress />
  </Box>
);

// Main app content with auth-aware routing
const AppContent: React.FC = () => {
  const {
    initialize,
    initialized,
    session,
    profile,
    profileLoading,
    isWaitlisted,
    isOnboardingComplete,
    isPasswordSet,
    isRecoverySession,
  } = useAuthStore();

  useCommandBarShortcut();

  useEffect(() => {
    initialize();
  }, [initialize]);

  // Wait for auth to initialize before rendering routes
  if (!initialized) {
    return <LoadingScreen />;
  }

  // Determine home route based on auth state
  const getHomeElement = () => {
    if (!session) {
      return <LandingPage />;
    }
    // Wait for profile to load before making routing decisions
    if (profileLoading) {
      return <LoadingScreen />;
    }
    // If this is a password recovery session, redirect to reset password page
    if (isRecoverySession) {
      return <Navigate to="/auth/reset-password" replace />;
    }
    // If invited user hasn't set a password yet, redirect to set password
    if (!isPasswordSet()) {
      return <Navigate to="/auth/reset-password?flow=invite" replace />;
    }
    // If authenticated but waitlisted, redirect to waitlist
    if (isWaitlisted()) {
      return <Navigate to="/waitlist" replace />;
    }
    // If authenticated but no username (OAuth user), redirect to onboarding
    if (!profile?.username) {
      return <Navigate to="/onboarding" replace />;
    }
    // If authenticated but onboarding not completed, redirect to complete profile
    if (!isOnboardingComplete()) {
      return <Navigate to="/onboarding/complete-profile" replace />;
    }
    return <Map />;
  };

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={getHomeElement()} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/confirm" element={<AuthConfirmPage />} />

        {/* Reset password - requires session from OTP verification */}
        <Route
          path="/auth/reset-password"
          element={
            <ProtectedRoute
              requirePasswordSet={false}
              requireUsername={false}
              requireApproval={false}
              requireOnboardingComplete={false}
            >
              <ResetPasswordPage />
            </ProtectedRoute>
          }
        />

        {/* Waitlist - authenticated but pending approval */}
        <Route
          path="/waitlist"
          element={
            <ProtectedRoute
              requirePasswordSet={false}
              requireUsername={false}
              requireApproval={false}
              requireOnboardingComplete={false}
            >
              <WaitlistPage />
            </ProtectedRoute>
          }
        />

        {/* Onboarding - authenticated but may lack username */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute
              requirePasswordSet={false}
              requireUsername={false}
              requireOnboardingComplete={false}
            >
              <OnboardingPage />
            </ProtectedRoute>
          }
        />

        {/* Complete profile - authenticated but onboarding not done */}
        <Route
          path="/onboarding/complete-profile"
          element={
            <ProtectedRoute requireOnboardingComplete={false}>
              <CompleteProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes - require full auth + username */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/:userId"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <SearchPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/feed"
          element={
            <ProtectedRoute>
              <ActivityFeedPage />
            </ProtectedRoute>
          }
        />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <CommandBar />
    </>
  );
};

function App() {
  return (
    <ThemeProvider theme={{ [MATERIAL_THEME_ID]: materialTheme }}>
      <JoyCssVarsProvider>
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
        </QueryClientProvider>
      </JoyCssVarsProvider>
    </ThemeProvider>
  );
}

export default App;
